import type { Unsubscribe } from 'firebase/firestore';

import { getSessionGeneration, isSessionGenerationCurrent } from 'src/auth/session-lifecycle';

import { handleInventoryBusinessField } from './inventory-sync.service';
import { subscribeBusinessDocumentSnapshots } from './business-document-snapshots';
import { handleDeviceControlBusinessField } from './business-control-local-state.service';
import {
  type BusinessDeviceSyncFields,
  handleDeviceSyncBusinessFields,
} from './device-sync-v4.service';

type BusinessDocumentListenerOptions = {
  enableInventorySync?: boolean;
  enableDeviceControl?: boolean;
  enableDeviceSync?: boolean;
  onDeviceSyncFieldsChange?: (fields: BusinessDeviceSyncFields) => Promise<void>;
  onProviderDetailsVersionChange?: (version: number) => void;
};

function readVersion(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  const { version } = value as { version?: unknown };
  return typeof version === 'number' ? version : null;
}

export function startBusinessDocumentListener(
  businessId: string,
  options: BusinessDocumentListenerOptions = {}
): Unsubscribe {
  if (!businessId) return () => {};
  const generation = getSessionGeneration();

  const {
    enableInventorySync = true,
    enableDeviceControl = true,
    enableDeviceSync = true,
    onProviderDetailsVersionChange,
    onDeviceSyncFieldsChange,
  } = options;
  return subscribeBusinessDocumentSnapshots(
    businessId,
    ({ data, exists }) => {
      if (!exists || !data) return;
      const handlers: Array<() => unknown> = [];
      if (enableInventorySync)
        handlers.push(() => handleInventoryBusinessField(data.inventory, businessId));
      if (enableDeviceSync)
        handlers.push(() =>
          (onDeviceSyncFieldsChange ?? handleDeviceSyncBusinessFields)({
            businessProfile: data.businessProfile,
            userProfile: data.userProfile,
            terminals: data.terminals,
            businessDevices: data.businessDevices,
            providerDetails: data.providerDetails,
            recurringPlans: data.recurringPlans,
          })
        );
      const providerDetailsVersion = readVersion(data.providerDetails);
      if (providerDetailsVersion !== null)
        handlers.push(() => onProviderDetailsVersionChange?.(providerDetailsVersion));
      if (enableDeviceControl)
        handlers.push(() =>
          handleDeviceControlBusinessField({ businessId, deviceControl: data.deviceControl })
        );
      // Run every domain even when an earlier one rejects. No rejected snapshot
      // callback escapes to Firestore or prevents device-control processing.
      Promise.allSettled(
        handlers.map((handler) =>
          Promise.resolve().then(() => {
            if (isSessionGenerationCurrent(generation)) return handler();
            return undefined;
          })
        )
      ).then((results) => {
        results.forEach((result) => {
          if (result.status === 'rejected')
            console.error('[BusinessDocumentListener] Update failed:', result.reason);
        });
      });
    },
    (error) => console.error('[BusinessDocumentListener] Listener error:', error)
  );
}
